import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Exercise,
    SpeakingExercise,
    ExerciseResponse,
    SpeakingExerciseResponse,
    ExerciseFilters,
    PaginationParams,
    ExerciseSubmission,
    ExerciseResult,
    SpeakingSubmission,
    SpeakingResult,
    Question,
} from '@/services/exerciseService';

interface ExerciseState {
    exercises: Exercise[];
    speakingExercises: SpeakingExercise[];
    currentExercise: Exercise | null;
    currentSpeakingExercise: SpeakingExercise | null;
    exerciseResults: ExerciseResult[];
    speakingResults: SpeakingResult[];
    assessmentQuestions: Question[];
    assessmentResult: {
        level: string;
        score: number;
        recommendations: string[];
        strengths: string[];
        weaknesses: string[];
    } | null;
    categories: string[];
    types: string[];
    filters: ExerciseFilters;
    pagination: {
        page: number;
        totalPages: number;
        total: number;
        limit: number;
    };
    isLoading: boolean;
    error: string | null;
}

const initialState: ExerciseState = {
    exercises: [],
    speakingExercises: [],
    currentExercise: null,
    currentSpeakingExercise: null,
    exerciseResults: [],
    speakingResults: [],
    assessmentQuestions: [],
    assessmentResult: null,
    categories: [],
    types: [],
    filters: {},
    pagination: {
        page: 1,
        totalPages: 1,
        total: 0,
        limit: 20,
    },
    isLoading: false,
    error: null,
};

const exerciseSlice = createSlice({
    name: 'exercise',
    initialState,
    reducers: {
        // Fetch Exercises Actions
        fetchExercisesRequest: (
            state: ExerciseState,
            action: PayloadAction<{ filters?: ExerciseFilters; pagination?: PaginationParams }>,
        ) => {
            state.isLoading = true;
            state.error = null;
            if (action.payload.filters) {
                state.filters = action.payload.filters;
            }
        },
        fetchExercisesSuccess: (state: ExerciseState, action: PayloadAction<ExerciseResponse>) => {
            state.isLoading = false;
            state.exercises = action.payload.exercises;
            state.pagination = {
                page: action.payload.page,
                totalPages: action.payload.totalPages,
                total: action.payload.total,
                limit: state.pagination.limit,
            };
            state.error = null;
        },
        fetchExercisesFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Exercise by ID Actions
        fetchExerciseByIdRequest: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchExerciseByIdSuccess: (state: ExerciseState, action: PayloadAction<Exercise>) => {
            state.isLoading = false;
            state.currentExercise = action.payload;
            state.error = null;
        },
        fetchExerciseByIdFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Submit Exercise Actions
        submitExerciseRequest: (state: ExerciseState, action: PayloadAction<ExerciseSubmission>) => {
            state.isLoading = true;
            state.error = null;
        },
        submitExerciseSuccess: (state: ExerciseState, action: PayloadAction<ExerciseResult>) => {
            state.isLoading = false;
            state.exerciseResults.unshift(action.payload);
            // Mark exercise as completed
            const exerciseIndex = state.exercises.findIndex((ex) => ex.id === action.payload.exerciseId);
            if (exerciseIndex !== -1) {
                state.exercises[exerciseIndex].isCompleted = true;
                state.exercises[exerciseIndex].score = action.payload.percentage;
                state.exercises[exerciseIndex].completedAt = action.payload.completedAt;
            }
            if (state.currentExercise?.id === action.payload.exerciseId) {
                state.currentExercise.isCompleted = true;
                state.currentExercise.score = action.payload.percentage;
                state.currentExercise.completedAt = action.payload.completedAt;
            }
            state.error = null;
        },
        submitExerciseFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Speaking Exercises Actions
        fetchSpeakingExercisesRequest: (
            state: ExerciseState,
            action: PayloadAction<{ filters?: ExerciseFilters; pagination?: PaginationParams }>,
        ) => {
            state.isLoading = true;
            state.error = null;
            if (action.payload.filters) {
                state.filters = action.payload.filters;
            }
        },
        fetchSpeakingExercisesSuccess: (state: ExerciseState, action: PayloadAction<SpeakingExerciseResponse>) => {
            state.isLoading = false;
            state.speakingExercises = action.payload.exercises;
            state.pagination = {
                page: action.payload.page,
                totalPages: action.payload.totalPages,
                total: action.payload.total,
                limit: state.pagination.limit,
            };
            state.error = null;
        },
        fetchSpeakingExercisesFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Speaking Exercise by ID Actions
        fetchSpeakingExerciseByIdRequest: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchSpeakingExerciseByIdSuccess: (state: ExerciseState, action: PayloadAction<SpeakingExercise>) => {
            state.isLoading = false;
            state.currentSpeakingExercise = action.payload;
            state.error = null;
        },
        fetchSpeakingExerciseByIdFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Submit Speaking Exercise Actions
        submitSpeakingExerciseRequest: (state: ExerciseState, action: PayloadAction<SpeakingSubmission>) => {
            state.isLoading = true;
            state.error = null;
        },
        submitSpeakingExerciseSuccess: (state: ExerciseState, action: PayloadAction<SpeakingResult>) => {
            state.isLoading = false;
            state.speakingResults.unshift(action.payload);
            // Mark speaking exercise as completed
            const exerciseIndex = state.speakingExercises.findIndex((ex) => ex.id === action.payload.exerciseId);
            if (exerciseIndex !== -1) {
                state.speakingExercises[exerciseIndex].isCompleted = true;
                state.speakingExercises[exerciseIndex].score = action.payload.score;
                state.speakingExercises[exerciseIndex].completedAt = action.payload.completedAt;
            }
            if (state.currentSpeakingExercise?.id === action.payload.exerciseId) {
                state.currentSpeakingExercise.isCompleted = true;
                state.currentSpeakingExercise.score = action.payload.score;
                state.currentSpeakingExercise.completedAt = action.payload.completedAt;
            }
            state.error = null;
        },
        submitSpeakingExerciseFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Toggle Speaking Favorite Actions
        toggleSpeakingFavoriteRequest: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = true;
            state.error = null;
        },
        toggleSpeakingFavoriteSuccess: (state: ExerciseState, action: PayloadAction<SpeakingExercise>) => {
            state.isLoading = false;
            const index = state.speakingExercises.findIndex((ex) => ex.id === action.payload.id);
            if (index !== -1) {
                state.speakingExercises[index] = action.payload;
            }
            if (state.currentSpeakingExercise?.id === action.payload.id) {
                state.currentSpeakingExercise = action.payload;
            }
            state.error = null;
        },
        toggleSpeakingFavoriteFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Exercise Results Actions
        fetchExerciseResultsRequest: (
            state: ExerciseState,
            action: PayloadAction<{ exerciseId?: string; pagination?: PaginationParams }>,
        ) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchExerciseResultsSuccess: (state: ExerciseState, action: PayloadAction<{ results: ExerciseResult[] }>) => {
            state.isLoading = false;
            state.exerciseResults = action.payload.results;
            state.error = null;
        },
        fetchExerciseResultsFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Speaking Results Actions
        fetchSpeakingResultsRequest: (
            state: ExerciseState,
            action: PayloadAction<{ exerciseId?: string; pagination?: PaginationParams }>,
        ) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchSpeakingResultsSuccess: (state: ExerciseState, action: PayloadAction<{ results: SpeakingResult[] }>) => {
            state.isLoading = false;
            state.speakingResults = action.payload.results;
            state.error = null;
        },
        fetchSpeakingResultsFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Assessment Actions
        fetchAssessmentQuestionsRequest: (state) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchAssessmentQuestionsSuccess: (state: ExerciseState, action: PayloadAction<Question[]>) => {
            state.isLoading = false;
            state.assessmentQuestions = action.payload;
            state.error = null;
        },
        fetchAssessmentQuestionsFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        submitAssessmentRequest: (
            state: ExerciseState,
            action: PayloadAction<{ questionId: string; answer: string | number }[]>,
        ) => {
            state.isLoading = true;
            state.error = null;
        },
        submitAssessmentSuccess: (
            state: ExerciseState,
            action: PayloadAction<{
                level: string;
                score: number;
                recommendations: string[];
                strengths: string[];
                weaknesses: string[];
            }>,
        ) => {
            state.isLoading = false;
            state.assessmentResult = action.payload;
            state.error = null;
        },
        submitAssessmentFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Categories Actions
        fetchExerciseCategoriesRequest: (state: ExerciseState) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchExerciseCategoriesSuccess: (state: ExerciseState, action: PayloadAction<string[]>) => {
            state.isLoading = false;
            state.categories = action.payload;
            state.error = null;
        },
        fetchExerciseCategoriesFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Fetch Types Actions
        fetchExerciseTypesRequest: (state: ExerciseState) => {
            state.isLoading = true;
            state.error = null;
        },
        fetchExerciseTypesSuccess: (state: ExerciseState, action: PayloadAction<string[]>) => {
            state.isLoading = false;
            state.types = action.payload;
            state.error = null;
        },
        fetchExerciseTypesFailure: (state: ExerciseState, action: PayloadAction<string>) => {
            state.isLoading = false;
            state.error = action.payload;
        },

        // Utility Actions
        setFilters: (state: ExerciseState, action: PayloadAction<ExerciseFilters>) => {
            state.filters = action.payload;
        },
        clearFilters: (state: ExerciseState) => {
            state.filters = {};
        },
        clearError: (state: ExerciseState) => {
            state.error = null;
        },
        setCurrentExercise: (state: ExerciseState, action: PayloadAction<Exercise | null>) => {
            state.currentExercise = action.payload;
        },
        setCurrentSpeakingExercise: (state: ExerciseState, action: PayloadAction<SpeakingExercise | null>) => {
            state.currentSpeakingExercise = action.payload;
        },
        clearAssessmentResult: (state: ExerciseState) => {
            state.assessmentResult = null;
        },
    },
});

export const {
    fetchExercisesRequest,
    fetchExercisesSuccess,
    fetchExercisesFailure,
    fetchExerciseByIdRequest,
    fetchExerciseByIdSuccess,
    fetchExerciseByIdFailure,
    submitExerciseRequest,
    submitExerciseSuccess,
    submitExerciseFailure,
    fetchSpeakingExercisesRequest,
    fetchSpeakingExercisesSuccess,
    fetchSpeakingExercisesFailure,
    fetchSpeakingExerciseByIdRequest,
    fetchSpeakingExerciseByIdSuccess,
    fetchSpeakingExerciseByIdFailure,
    submitSpeakingExerciseRequest,
    submitSpeakingExerciseSuccess,
    submitSpeakingExerciseFailure,
    toggleSpeakingFavoriteRequest,
    toggleSpeakingFavoriteSuccess,
    toggleSpeakingFavoriteFailure,
    fetchExerciseResultsRequest,
    fetchExerciseResultsSuccess,
    fetchExerciseResultsFailure,
    fetchSpeakingResultsRequest,
    fetchSpeakingResultsSuccess,
    fetchSpeakingResultsFailure,
    fetchAssessmentQuestionsRequest,
    fetchAssessmentQuestionsSuccess,
    fetchAssessmentQuestionsFailure,
    submitAssessmentRequest,
    submitAssessmentSuccess,
    submitAssessmentFailure,
    fetchExerciseCategoriesRequest,
    fetchExerciseCategoriesSuccess,
    fetchExerciseCategoriesFailure,
    fetchExerciseTypesRequest,
    fetchExerciseTypesSuccess,
    fetchExerciseTypesFailure,
    setFilters,
    clearFilters,
    clearError,
    setCurrentExercise,
    setCurrentSpeakingExercise,
    clearAssessmentResult,
} = exerciseSlice.actions;

export default exerciseSlice.reducer;
